import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Input } from '@angular/core';
import { MediaModule, IconModule, ICON_TYPE, SpinnerModule } from '@spartacus/storefront';
import { Subscription, of, throwError, combineLatest, Observable } from "rxjs";
import { switchMap, catchError, map, distinctUntilChanged, tap, filter } from 'rxjs/operators';
import { GlobalMessageService, GlobalMessageType, UrlModule } from '@spartacus/core';
import { environment } from 'src/environments/environment';
import { GenEnergyManagementProduct, GenEnergyManagementProductHeaders } from 'src/app/generac/models/product.model';
import { GenBaseStoreService } from 'src/app/generac/services/base-store.service';
import { GenProductService } from 'src/app/generac/services/product.service';
import { CommonModule } from '@angular/common';
import { GenAddToCartModule } from 'src/app/generac/features/cart/product-add-to-cart/product-add-to-cart.module';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { GenLoadingEnum } from 'src/app/generac/enums/loading.enum';
import { GenGaListNames } from 'src/app/generac/enums/ga-list-names.enum';
import { GenInfoMessageComponent } from '../info-message/info-message.component';
import { GaItem, GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';
import { normalizeSlug } from '../../../shared/helpers/slug.helper';
import { isEqual } from '../../helpers/is-equal';

@Component({
  selector: 'gen-energy-management-system',
  templateUrl: './energy-management-system.component.html',
  standalone: true,
  imports: [MediaModule, CommonModule, GenInfoMessageComponent, IconModule, GenAddToCartModule, RouterModule, UrlModule, SpinnerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class GenEnergyManagementSystemComponent implements OnInit, OnDestroy {

  readonly itemsPerPage: number = 4;
  readonly nextIcon: string = ICON_TYPE.CARET_RIGHT;
  readonly previousIcon: string = ICON_TYPE.CARET_LEFT;

  @Input() gaProductCategories: { name: string }[];
  @Input() gaEntrySource: string;

  title: string;
  productList: GenEnergyManagementProduct[] = [];
  headers: GenEnergyManagementProductHeaders[] = [];
  LoadingEnum = GenLoadingEnum;
  loading = GenLoadingEnum.Idle;
  callForPricingMessage: number | null = null;
  startIndex = 0;
  endIndex = this.itemsPerPage;
  tooltipMessage: string;
  activeIndex: number | null = null;
  gaListName = GenGaListNames.VCT;
  gaItems: GaItem[] = [];

  protected subscription = new Subscription();

  constructor(
    private productService: GenProductService,
    private userAccount: UserAccountFacade,
    private baseStoreService: GenBaseStoreService,
    protected globalMessageService: GlobalMessageService,
    private cdr: ChangeDetectorRef,
    protected activatedRoute: ActivatedRoute,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
  ) { }

  ngOnInit(): void {
    this.subscription.add(
      this.activatedRoute.params.pipe(
        filter(params => !!params),
        switchMap(params => of(params?.productCode)),
        switchMap((productCode: string) => this.loadComparisonData(productCode))
      ).subscribe()
    );

    this.subscribeToBaseInfo();
  }

  loadComparisonData(productCode: string): Observable<any> {
    this.loading = GenLoadingEnum.Loading;

    return this.userAccount.get().pipe(
      filter(user => !!user?.uid),
      switchMap(user => combineLatest([this.productService.getValueComparisonData(user.uid, productCode), of(user)])),
      switchMap(([comparisonData, user]) => {
        this.title = comparisonData?.name
        const products = comparisonData?.productDetails;
        if (!products || products.length === 0) return of([]);

        const netPriceCalls = products.map((product: any) =>
          this.productService.getCPIPrice(user.uid, product).pipe(
            map(netPrice => ({ ...product, netPrice })),
            catchError(err => of({ ...product, netPrice: null }))
          )
        );

        return netPriceCalls.length ? combineLatest([...netPriceCalls]) : of([]);
      }),
      distinctUntilChanged(isEqual),
      tap((products) => {
        this.loading = GenLoadingEnum.Loaded;

        if (!products.length) {
          this.productList = [];
          this.cdr.markForCheck();
          return;
        }

        this.googleAnalyticsService.sendViewItemListEvent(products, this.gaListName);

        this.headers = [
          { key: 'name', displayName: '' },
          { key: 'productDetails', displayName: '' },
          { key: 'price', displayName: 'Price' },
          ...this.getDynamicHeader(products[0].specifications)
        ]

        this.productList = products.map((product: any) => this.transformEnergyProduct(product));
        this.cdr.markForCheck();
      }),
      catchError((err: any) => {
        const errorMessage = err?.error?.errors?.[0]?.message || 'An unknown error occurred';
        this.globalMessageService.add(errorMessage, GlobalMessageType.MSG_TYPE_ERROR);
        this.loading = GenLoadingEnum.Failed;
        this.cdr.markForCheck();
        return throwError(() => err);
      }),
    )
  }

  getDynamicHeader(specifications: { key: string, value: { type: string, value: string } }[]): GenEnergyManagementProductHeaders[] {
    return specifications.map((spec: any) => {
      const cleanKey = spec.key.replace(/\s+/g, '').toLowerCase();
      return {
        key: cleanKey,
        displayName: spec.key
      };
    });
  }

  transformEnergyProduct(product: any): GenEnergyManagementProduct {
    const primaryImage = product?.images?.find(
      (img: any) => img.format === 'product' && img.imageType === 'PRIMARY'
    );

    let imageContainer = {};
    if (primaryImage) {
      imageContainer = {
        altText: primaryImage.altText,
        url: environment.backend.occ.baseUrl + primaryImage.url
      }
    }

    const specsObj = product.specifications.reduce((acc: any, spec: any) => {
      const cleanKey = spec.key.replace(/\s+/g, '').toLowerCase();
      acc[cleanKey] = spec?.value?.value;
      return acc;
    }, {});

    return {
      code: product.code,
      name: product.name,
      slug: normalizeSlug(product.name),
      productDetails: {
        images: imageContainer,
        purchasable: product?.purchasable
      },
      price: {
        msrp: product?.price?.formattedValue || 'NA',
        net: product?.netPrice?.value ? `$${product?.netPrice?.value}` : 'NA',
        value: product?.price?.value,
        currencyIso: product?.price?.currencyIso
      },
      ...specsObj
    };
  }

  private subscribeToBaseInfo() {
    this.subscription.add(this.baseStoreService.getTooltipMessage().subscribe(message => {
      this.tooltipMessage = message?.message;
    }));
  }

  goToNextPage(): void {
    if (this.endIndex < this.productList.length) {
      this.startIndex = this.endIndex;
      this.endIndex = Math.min(this.endIndex + this.itemsPerPage, this.productList.length);
    }
  }

  goToPreviousPage(): void {
    if (this.startIndex > 0) {
      this.endIndex = this.startIndex;
      this.startIndex = Math.max(this.startIndex - this.itemsPerPage, 0);
    }
  }

  selectItem(item: any): void {
    const product = {
      ...item,
      categories: this.gaProductCategories,
    };

    this.googleAnalyticsService.sendGaEvent('select_item', {
      items: [this.googleAnalyticsService.mapProductToGaItem(product, 0, undefined, this.gaListName, this.gaListName)],
      item_list_name: this.gaListName || '',
      item_list_id: this.gaListName ? this.gaListName.toLowerCase().trim().replace(/ /g, '_') : '',
      currency: product.price?.currencyIso,
    });
  }


  trackByProductCode(index: number, product: GenEnergyManagementProduct): string {
    return product.code;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}